import { hasDismissOwner } from "@/lib/a11y/dismissOwner";

// Outside-press listener for overlays above the slotting board.
//
// Dual-event, bubble phase — both are load-bearing:
//  - pressDrag preventDefault()s mouse pointerdown, which suppresses the compat
//    mousedown on every board drag surface → "mousedown"-only never fires there.
//  - Tile action buttons stop pointerdown propagation (drag guard) but let the
//    compat mousedown bubble → "pointerdown"-only misses them.
// Bubble (not capture) so portal shields that stopPropagation at body
// (DatePicker stopIfInPortal) keep protecting their contents.
// Handlers must be idempotent: a plain mouse click fires BOTH events.
export function addOutsidePressListener(
  handler: (e: MouseEvent) => void,
  /**
   * The host's own subtree. With it, only a picker list open INSIDE the host
   * makes the host stand down; without it the question is asked of the whole
   * document, and a list open anywhere else on the page costs one extra press
   * (the list's own blur closes it; the host closes on the next). Pass it
   * wherever the host has a ref — every host that contains a picker does.
   */
  root?: () => ParentNode | null,
): () => void {
  // An open child overlay owns the press. Picker lists used to cover the
  // viewport with a click catcher while open, which swallowed this press
  // entirely; that catcher is gone (it was also eating the first click on
  // Save), so the rule it enforced by accident is now stated. One press, one
  // dismissal: the list closes on its own blur, the popover stays.
  const guarded = (e: MouseEvent) => {
    if (hasDismissOwner(root ? root() : undefined)) return;
    handler(e);
  };
  document.addEventListener("pointerdown", guarded);
  document.addEventListener("mousedown", guarded);
  return () => {
    document.removeEventListener("pointerdown", guarded);
    document.removeEventListener("mousedown", guarded);
  };
}
